import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Loading from "../../Common/Loading";

type Country = "ALL" | "AU" | "CA" | "UNI" | "MD";

interface LessonRow {
  lsnCode: string;
  lsnName: string;
  assignName: string;
  loggedDate: string;
  country?: string;
}

interface ReportResponse {
  rows: LessonRow[];
  includeGroupColumn: boolean;
  fromDate: string;
  toDate: string;
  country: Country;
}

const COUNTRY_OPTIONS: Country[] = ["ALL", "AU", "CA", "UNI", "MD"];

const API_BASE = process.env.REACT_APP_DOCUMENT_LINK;

const SESSION_KEYS_TO_CLEAR = [
  "SES_LOGGEDIN",
  "SES_TESTLESSONS",
  "SES_SCHOOLCODE",
  "SES_NAVIGATEDFROM",
  "SES_SELDICCODE",
  "SES_SELUNITCODE",
  "SES_SELLESSONCODE",
  "TMP_VIEWWHAT",
  "TMP_BlobFolder",
  "SES_Fixed",
  "SES_UserVarSess",
  "TMP_VIEWQNLESSONS",
  "TMP_VIEWQNTYPE",
  "TMP_TMPTESTCODE",
];

function toDateInputValue(d: Date): string {
  return d.toISOString().slice(0, 10); // yyyy-MM-dd, matches txtCalFromDate/ToDate format
}

function defaultFromDate(): string {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return toDateInputValue(d);
}

function defaultToDate(): string {
  return toDateInputValue(new Date());
}

const DownloadIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#495057"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3v12" />
    <path d="M7 10l5 5 5-5" />
    <path d="M4 20h16" />
  </svg>
);

// Shared inline styles for header cells: background/color/border live on the
// <th> itself (not the parent <tr>) because position:sticky only carries the
// element it's applied to along when scrolling — a background painted on the
// row disappears from view the moment the row scrolls up, even though the
// sticky th text stays. Painting the background on the th fixes that.
const thStyle: React.CSSProperties = {
  border: "1px solid black",
  padding: "5px 10px",
  backgroundColor: "#343a40",
  color: "white",
  textAlign: "center",
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const tdStyle: React.CSSProperties = {
  border: "1px solid black",
  padding: "5px 10px",
  whiteSpace: "nowrap",
};

const LiveLessonsReport: React.FC = () => {
  const navigate = useNavigate();
  const tableRef = useRef<HTMLTableElement>(null);

  const [country, setCountry] = useState<Country>("ALL");
  const [fromDate, setFromDate] = useState<string>(defaultFromDate());
  const [toDate, setToDate] = useState<string>(defaultToDate());
  const [rows, setRows] = useState<LessonRow[]>([]);
  const [includeGroupColumn, setIncludeGroupColumn] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  useEffect(() => {
    document.body.style.backgroundColor = "#fff";
  }, []);

  const runSearch = useCallback(
    async (searchCountry: Country, searchFrom: string, searchTo: string) => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({
          country: searchCountry,
          from_date: searchFrom,
          to_date: searchTo,
        });
        const resp = await fetch(`${API_BASE}api/reports/yla-lessons/?${params.toString()}`, {
          credentials: "include",
        });
        if (!resp.ok) {
          throw new Error(`Request failed with status ${resp.status}`);
        }
        const data: ReportResponse = await resp.json();
        setRows(data.rows);
        setIncludeGroupColumn(data.includeGroupColumn);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load report");
        setRows([]);
      } finally {
        setLoading(false);
        setHasSearched(true);
      }
    },
    []
  );

  // Mirrors: If Not IsPostBack Then set defaults, btngo_Click(Nothing, EventArgs.Empty)
  useEffect(() => {
    runSearch(country, fromDate, toDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchClick = () => {
    runSearch(country, fromDate, toDate);
  };

  // Mirrors DownloadTableToXl(filename) exactly.
  const handleDownload = () => {
    const table = tableRef.current;
    if (!table) return;

    const html = table.outerHTML;
    const blob = new Blob(["\ufeff", html], {
      type: "application/vnd.ms-excel;charset=utf-8;",
    });

    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = "YLA_Report.xls";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Mirrors btnBack_Click: clear session state, redirect to SysAdminNew.
  const handleClose = () => {
    SESSION_KEYS_TO_CLEAR.forEach((key) => sessionStorage.removeItem(key));
    navigate("/SysAdmin");
  };

  const showDownload = hasSearched && !loading && rows.length > 0;
  const showNoRecords = hasSearched && !loading && !error && rows.length === 0;
  const showTable = !loading && !error;

  return (
    <div className="yla-report">
      {/* ===== TITLE ===== */}
      <div className="yla-card yla-title-card">
        <div className="yla-title-bar">Live Lessons Report</div>
      </div>

      {/* ===== FILTER SECTION ===== */}
      <div className="yla-card yla-filter-card">
        <div className="yla-filter-row">
          <div className="yla-filter-left">
            {/* COUNTRY */}
            <div className="yla-radio-group" role="radiogroup" aria-label="Country">
              {COUNTRY_OPTIONS.map((opt) => (
                <label key={opt} className="yla-radio-label">
                  <input
                    type="radio"
                    name="rblCountry"
                    value={opt}
                    checked={country === opt}
                    onChange={() => setCountry(opt)}
                    disabled={loading}
                  />
                  <span>{opt}</span>
                </label>
              ))}
            </div>

            {/* FROM DATE */}
            <div className="yla-date-field">
              <label>From</label>
              <input
                type="date"
                className="yla-date-input"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                disabled={loading}
              />
            </div>

            {/* TO DATE */}
            <div className="yla-date-field">
              <label>To</label>
              <input
                type="date"
                className="yla-date-input"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                disabled={loading}
              />
            </div>

            {/* SEARCH BUTTON */}
            <button
              type="button"
              className="yla-btn yla-btn-primary"
              onClick={handleSearchClick}
              disabled={loading}
            >
              Search
            </button>
          </div>

          {/* RIGHT SIDE CLOSE + DOWNLOAD BUTTON */}
          <div className="yla-filter-right">
            {showDownload && (
              <button
                type="button"
                className="yla-btn yla-btn-icon"
                title="Download"
                onClick={handleDownload}
              >
                <DownloadIcon />
              </button>
            )}
            <button
              type="button"
              className="yla-btn yla-btn-outline-danger"
              onClick={handleClose}
              disabled={loading}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* ===== TABLE DATA ===== */}
      <div className="yla-card yla-table-card">
        <div className="yla-table-scroll">
          {loading && (
            <div className="yla-loading-wrap">
              <Loading />
            </div>
          )}

          {!loading && error && <div className="yla-alert yla-alert-danger">{error}</div>}

          {showTable && (
            <table
              id="exportTable"
              ref={tableRef}
              border={1}
              style={{
                borderCollapse: "collapse",
                fontSize: 16,
                fontFamily: '"Times New Roman", Times, serif',
              }}
            >
              <thead>
                <tr>
                  <th style={thStyle}>SL No</th>
                  <th style={thStyle}>Lesson Code</th>
                  <th style={thStyle}>Lesson Name</th>
                  <th style={thStyle}>Live made by</th>
                  <th style={thStyle}>Live made on</th>
                  {includeGroupColumn && <th style={thStyle}>Group</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={`${row.lsnCode}-${idx}`}>
                    <td style={tdStyle}>{idx + 1}</td>
                    <td style={tdStyle}>{row.lsnCode}</td>
                    <td style={tdStyle}>{row.lsnName}</td>
                    <td style={tdStyle}>{row.assignName}</td>
                    <td style={tdStyle}>{row.loggedDate}</td>
                    {includeGroupColumn && <td style={tdStyle}>{row.country ?? ""}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {showNoRecords && <div className="yla-alert yla-alert-warning">No records found</div>}
        </div>
      </div>

      {/* ===== IN-FILE CSS — no Bootstrap or any external stylesheet required ===== */}
      <style>{`
        .yla-report {
          font-family: Arial, Helvetica, sans-serif;
          padding: 0 24px;
          margin-top: 16px;
          box-sizing: border-box;
        }
        .yla-report *, .yla-report *::before, .yla-report *::after {
          box-sizing: border-box;
        }

        .yla-card {
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
          margin-bottom: 16px;
        }

        .yla-title-card {
          overflow: hidden;
        }
        .yla-title-bar {
          background: linear-gradient(45deg, #008080, #0d6efd);
          color: #fff;
          font-weight: 600;
          font-size: 18px;
          padding: 14px 20px;
        }

        .yla-filter-card {
          padding: 14px 20px;
        }
        .yla-filter-row {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }
        .yla-filter-left {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 20px;
        }
        .yla-filter-right {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .yla-radio-group {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 16px;
        }
        .yla-radio-label {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 400;
          cursor: pointer;
        }
        .yla-radio-label input[type="radio"] {
          margin: 0;
          cursor: pointer;
        }

        .yla-date-field {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .yla-date-field label {
          font-weight: 600;
          white-space: nowrap;
        }
        .yla-date-input {
          width: 170px;
          padding: 6px 10px;
          border: 1px solid #ced4da;
          border-radius: 6px;
          font-size: 14px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .yla-btn {
          padding: 7px 18px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          border: 1px solid transparent;
          cursor: pointer;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }
        .yla-btn:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }
        .yla-btn-primary {
          background: #0d6efd;
          border-color: #0d6efd;
          color: #fff;
        }
        .yla-btn-primary:hover:not(:disabled) {
          background: #0b5ed7;
          border-color: #0a58ca;
        }
        .yla-btn-outline-danger {
          background: #fff;
          border-color: #dc3545;
          color: #dc3545;
        }
        .yla-btn-outline-danger:hover:not(:disabled) {
          background: #dc3545;
          color: #fff;
        }
        .yla-btn-icon {
          background: #f8f9fa;
          border-color: #ced4da;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 7px 10px;
        }
        .yla-btn-icon:hover {
          background: #e9ecef;
        }

        /* Table sizes to its own content (like the original VB page) instead
           of stretching to fill the card — only the scroll wrapper spans the
           card width, and it scrolls both ways once content overflows it. */
        .yla-table-card {
          padding: 0;
          overflow: hidden;
        }
        .yla-table-scroll {
          width: 100%;
          max-height: 75vh;
          overflow: auto;
          padding: 8px;
        }
        .yla-table-scroll table#exportTable {
          width: auto;
          border-collapse: collapse;
        }
        .yla-table-scroll table#exportTable tbody tr:hover {
          background: #f2f7ff;
          transition: 0.2s;
        }

        .yla-loading-wrap {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 60px 0;
        }

        .yla-alert {
          margin: 12px 0;
          padding: 10px 16px;
          border-radius: 8px;
          text-align: center;
          font-size: 14px;
        }
        .yla-alert-danger {
          background: #f8d7da;
          color: #842029;
          border: 1px solid #f5c2c7;
        }
        .yla-alert-warning {
          background: #fff3cd;
          color: #664d03;
          border: 1px solid #ffecb5;
        }
      `}</style>
    </div>
  );
};

export default LiveLessonsReport;